/**
 * AI tutor chat (M4): reusable panel in the course/lesson view and in
 * the tutor page. Renders the thread (markdown+code in the responses),
 * "typing" indicator, and "unavailable" states (feature off,
 * AI unconfigured, quota exhausted). Server-only data via REST.
 */
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Send, Sparkles, TriangleAlert } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { type MessageDto, useAvailability, useConversation, useSendChat } from "./api";
import { TutorMarkdown } from "./markdown";

interface Turn {
  role: "user" | "assistant";
  content: string;
}

export interface TutorChatProps {
  org?: string;
  courseId?: string;
  lessonId?: string;
  cohortId?: string;
  /** Continues an existing conversation (loads its history). */
  conversationId?: string;
  /** Compact height for the side drawer. */
  compact?: boolean;
}

export function TutorChat(props: TutorChatProps) {
  const { t } = useI18n();
  const [turns, setTurns] = createSignal<Turn[]>([]);
  const [conversationId, setConversationId] = createSignal<string | undefined>(
    // SINGLE read of props for the initial value (the component remounts per
    // conversation via a keyed <Show> in the route); the thread syncs via effect.
    // oxlint-disable-next-line solid/reactivity
    props.conversationId,
  );
  const [input, setInput] = createSignal("");
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  const availability = useAvailability(
    () => props.org,
    () => props.courseId,
  );
  const existing = useConversation(() => props.conversationId);
  const send = useSendChat();

  // Seeds the history when an existing conversation is opened.
  createEffect(() => {
    const detail = existing.data;
    if (detail) {
      setConversationId(detail.id);
      setTurns(detail.messages.map((m: MessageDto) => ({ role: m.role, content: m.content })));
    }
  });

  const unavailableReason = createMemo<string | null>(() => {
    const a = availability.data;
    if (!a || a.available) {
      return null;
    }
    return a.reason ?? "unavailable";
  });

  const reasonText = (reason: string): string => {
    switch (reason) {
      case "feature_off": {
        return t(msg`The AI tutor is not active for your academy.`);
      }
      case "unconfigured": {
        return t(msg`Your academy has not configured the AI integration yet.`);
      }
      case "over_quota": {
        return t(msg`Your academy's monthly AI quota ran out. Come back next month.`);
      }
      default: {
        return t(msg`The AI tutor is not available right now.`);
      }
    }
  };

  const submit = (event: Event) => {
    event.preventDefault();
    const message = input().trim();
    if (!message || send.isPending) {
      return;
    }
    setErrorMsg(null);
    setTurns((prev) => [...prev, { role: "user", content: message }]);
    setInput("");
    send.mutate(
      {
        org: props.org,
        courseId: props.courseId,
        lessonId: props.lessonId,
        cohortId: props.cohortId,
        conversationId: conversationId(),
        message,
      },
      {
        onSuccess: (res) => {
          setConversationId(res.conversationId);
          setTurns((prev) => [...prev, { role: "assistant", content: res.reply }]);
        },
        onError: (err: unknown) => {
          setErrorMsg(err instanceof Error ? err.message : t(msg`Could not send the message.`));
        },
      },
    );
  };

  return (
    <div
      class="flex flex-col"
      classList={{ "h-[70vh]": !props.compact, "h-[60vh]": props.compact }}
    >
      <Show
        when={!unavailableReason()}
        fallback={
          <div
            class="m-4 flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm"
            data-testid="ai-tutor-unavailable"
          >
            <TriangleAlert class="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <p>{reasonText(unavailableReason() ?? "unavailable")}</p>
          </div>
        }
      >
        {/* Hilo */}
        <div class="flex-1 space-y-4 overflow-y-auto px-1 py-2" data-testid="ai-tutor-thread">
          <Show when={turns().length === 0}>
            <div class="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground">
              <Sparkles class="h-8 w-8 text-primary" />
              <p class="text-sm">
                {t(msg`Ask the tutor about this course. It answers based on the material.`)}
              </p>
            </div>
          </Show>
          <For each={turns()}>
            {(turn) => (
              <div
                class="flex"
                classList={{ "justify-end": turn.role === "user" }}
                data-testid={`ai-turn-${turn.role}`}
              >
                <div
                  class="max-w-[85%] rounded-2xl px-4 py-2"
                  classList={{
                    "bg-primary text-primary-foreground": turn.role === "user",
                    "bg-muted/50 border border-border/40": turn.role === "assistant",
                  }}
                >
                  <Show
                    when={turn.role === "assistant"}
                    fallback={<p class="text-sm">{turn.content}</p>}
                  >
                    <TutorMarkdown text={turn.content} />
                  </Show>
                </div>
              </div>
            )}
          </For>
          <Show when={send.isPending}>
            <div class="flex" data-testid="ai-tutor-typing">
              <div class="rounded-2xl border border-border/40 bg-muted/50 px-4 py-3">
                <span class="flex gap-1">
                  <span class="h-2 w-2 animate-bounce rounded-full bg-foreground/40 [animation-delay:-0.3s]" />
                  <span class="h-2 w-2 animate-bounce rounded-full bg-foreground/40 [animation-delay:-0.15s]" />
                  <span class="h-2 w-2 animate-bounce rounded-full bg-foreground/40" />
                </span>
              </div>
            </div>
          </Show>
        </div>

        <Show when={errorMsg()}>
          <p class="px-2 pb-1 text-xs text-destructive" data-testid="ai-tutor-error">
            {errorMsg()}
          </p>
        </Show>

        {/* Entrada */}
        <form class="flex items-end gap-2 border-t border-border/40 pt-3" onSubmit={submit}>
          <textarea
            class="min-h-[44px] max-h-40 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder={t(msg`Type your question…`)}
            value={input()}
            data-testid="ai-tutor-input"
            onInput={(e) => setInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                submit(e);
              }
            }}
          />
          <Button
            type="submit"
            disabled={!input().trim() || send.isPending}
            data-testid="ai-tutor-send"
          >
            <Send class="h-4 w-4" />
            <span class="sr-only">{t(msg`Send`)}</span>
          </Button>
        </form>
        <p class="pt-2 text-center text-[11px] text-muted-foreground">
          {t(msg`The tutor can make mistakes; check anything important with your teacher.`)}
        </p>
      </Show>
    </div>
  );
}
